import SportIcon from '@/components/SportIcon';
import { useEffect, useState, useRef } from 'react';
import { Calendar, Plus, Search, Trophy, MoreHorizontal, Eye, Trash2, Zap, Check, ChevronDown, CalendarDays, Upload, Users, ArrowRight, ArrowLeft, Palette, Copy, Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api, { BACKEND_URL } from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽',
};

// Default sport format configs — pre-filled in wizard Step 2
const SPORT_FORMAT_DEFAULTS = {
  table_tennis: { singles_count:6, doubles_count:0, platinum_count:2, win_pts:2, draw_pts:1, loss_pts:0 },
  badminton:    { singles_count:0, doubles_count:3, platinum_count:2, win_pts:2, draw_pts:1, loss_pts:0 },
  chess:        { singles_count:3, doubles_count:0, platinum_count:1, win_pts:2, draw_pts:1, loss_pts:0 },
  carrom:       { singles_count:0, doubles_count:5, platinum_count:4, win_pts:2, draw_pts:1, loss_pts:0 },
  pickleball:   { singles_count:0, doubles_count:5, platinum_count:4, win_pts:2, draw_pts:1, loss_pts:0 },
  squash:       { singles_count:5, doubles_count:0, platinum_count:1, win_pts:2, draw_pts:1, loss_pts:0 },
  cricket:      { singles_count:1, doubles_count:0, platinum_count:0, win_pts:2, draw_pts:1, loss_pts:0 },
  football:     { singles_count:1, doubles_count:0, platinum_count:0, win_pts:3, draw_pts:1, loss_pts:0 },
};

const FORMATS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'single_elim', label: 'Knockout' },
  { value: 'groups_plus_ko', label: 'Groups + Semis' },
];

const StatusBadge = ({ status }) => {
  const config = {
    draft: { label: 'Draft', className: 'bg-[#1d2230] text-[#8a93a3] border-[#252a37]' },
    upcoming: { label: 'Upcoming', className: 'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border-[rgba(59,130,246,0.35)]' },
    active: { label: 'Active', className: 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]' },
    live: { label: 'Live', className: 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]' },
    completed: { label: 'Completed', className: 'bg-[rgba(139,92,246,0.15)] text-[#8b5cf6] border-[rgba(139,92,246,0.35)]' },
  };
  const c = config[status?.toLowerCase()] || config.draft;
  return <Badge className={`${c.className} border`}>{c.label}</Badge>;
};

const CALENDAR_CLASSES = {
  months: 'flex flex-col space-y-4',
  month: 'space-y-4',
  caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-sm font-medium text-[#f0f2f7]',
  nav: 'space-x-1 flex items-center',
  nav_button: 'h-7 w-7 p-0 text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#252a37] rounded-md border-0 bg-transparent inline-flex items-center justify-center',
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'text-[#8a93a3] rounded-md w-9 font-normal text-[0.8rem] text-center',
  row: 'flex w-full mt-2',
  cell: 'h-9 w-9 text-center text-sm p-0',
  day: 'h-9 w-9 p-0 font-normal text-[#f0f2f7] rounded-md hover:bg-[#252a37] inline-flex items-center justify-center',
  day_selected: 'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] hover:text-[#0b0f18] font-bold',
  day_today: 'bg-[#1d2230] text-[#f5c518] font-semibold',
  day_outside: 'text-[#8a93a3]/30 hover:bg-transparent',
  day_disabled: 'text-[#8a93a3]/20 hover:bg-transparent',
  day_hidden: 'invisible',
};

const DatePickerField = ({ value, onChange, placeholder = 'Pick a date' }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + 'T00:00') : undefined;
  const display = selected
    ? selected.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`w-full justify-start text-left font-normal bg-[#1d2230] border-[#252a37] hover:bg-[#252a37] hover:text-[#f0f2f7] ${display ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}>
          <CalendarDays className="h-4 w-4 mr-2 flex-shrink-0" />
          {display || placeholder}
          <ChevronDown className="h-4 w-4 ml-auto opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#10131c] border-[#252a37] shadow-2xl" align="start">
        <CalendarPicker
          mode="single"
          selected={selected}
          onSelect={day => {
            if (day) {
              const yyyy = day.getFullYear();
              const mm   = String(day.getMonth() + 1).padStart(2, '0');
              const dd   = String(day.getDate()).padStart(2, '0');
              onChange(`${yyyy}-${mm}-${dd}`);
            }
            setOpen(false);
          }}
          initialFocus
          classNames={CALENDAR_CLASSES}
        />
      </PopoverContent>
    </Popover>
  );
};

// ─── Team Setup Row (Step 2 of wizard) ────────────────────────────
const TEAM_COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];

const emptyTeam = (idx) => ({
  name: '', short_name: '', color: TEAM_COLORS[idx % TEAM_COLORS.length],
  logo_b64: null, logo_preview: null, sponsor: '',
});

const autoCode = (name) =>
  name.split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 5) || name.slice(0, 4).toUpperCase();

const TeamFormRow = ({ idx, data, onChange }) => {
  const fileRef = useRef(null);
  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    new Promise((res) => {
      const reader = new FileReader();
      reader.onload = (ev) => res(ev.target.result);
      reader.readAsDataURL(file);
    }).then(b64 => onChange(idx, { ...data, logo_b64: b64, logo_preview: b64 }));
  };
  return (
    <div className="flex items-center gap-2 p-2.5 bg-[#1d2230] rounded-lg border border-[#252a37]">
      <span className="w-5 h-5 rounded-full bg-[#252a37] flex items-center justify-center text-[10px] text-[#8a93a3] flex-shrink-0 font-bold">{idx + 1}</span>

      {/* Logo */}
      <div
        onClick={() => fileRef.current?.click()}
        className="h-9 w-9 rounded-lg cursor-pointer overflow-hidden flex items-center justify-center border border-[#252a37] hover:border-[#f5c518] transition-colors flex-shrink-0"
        style={{ backgroundColor: data.color }}
        title="Click to upload team logo"
      >
        {data.logo_preview
          ? <img src={data.logo_preview} className="h-full w-full object-cover" alt="logo" />
          : <Upload className="h-3.5 w-3.5 text-white/60" />}
      </div>
      <input type="file" accept="image/*" ref={fileRef} hidden onChange={handleLogo} />

      {/* Color */}
      <input
        type="color" value={data.color}
        onChange={e => onChange(idx, { ...data, color: e.target.value })}
        className="h-8 w-8 rounded cursor-pointer p-0.5 border-0 bg-transparent flex-shrink-0"
        title="Team color"
      />

      {/* Name */}
      <input
        value={data.name}
        onChange={e => {
          const name = e.target.value;
          const auto = !data.short_name || data.short_name === autoCode(data.name);
          onChange(idx, { ...data, name, short_name: auto ? autoCode(name) : data.short_name });
        }}
        placeholder={`Team ${idx + 1} name *`}
        className="flex-1 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-2 focus:outline-none focus:border-[rgba(245,197,24,0.4)] min-w-0"
      />

      {/* Short code */}
      <input
        value={data.short_name}
        onChange={e => onChange(idx, { ...data, short_name: e.target.value.toUpperCase().slice(0, 5) })}
        placeholder="CODE" maxLength={5}
        className="w-14 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 text-center uppercase focus:outline-none focus:border-[rgba(245,197,24,0.4)]"
      />

      {/* Sponsor */}
      <input
        value={data.sponsor}
        onChange={e => onChange(idx, { ...data, sponsor: e.target.value })}
        placeholder="Sponsor (optional)"
        className="w-28 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 focus:outline-none focus:border-[rgba(245,197,24,0.4)] hidden sm:block"
      />
    </div>
  );
};

export default function TournamentsPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  // Use live membership slug when available — guards against stale persisted state
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [tournaments, setTournaments] = useState([]);
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateTarget, setGenerateTarget] = useState(null);
  const [genTeams, setGenTeams] = useState([]);
  const [genSeeding, setGenSeeding] = useState('linear');
  const [generating, setGenerating] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1=details, 2=sport-formats, 3=teams
  // Clone modal state
  const [cloneTarget, setCloneTarget]   = useState(null);
  const [cloneName, setCloneName]       = useState('');
  const [cloning, setCloning]           = useState(false);
  const [numTeams, setNumTeams] = useState('4');
  const [teamForms, setTeamForms] = useState([]);
  const [sportFormats, setSportFormats] = useState({}); // keyed by sport_code
  const [formData, setFormData] = useState({
    name: '', sport_codes: [], format: 'groups_plus_ko',
    start_date: '', end_date: '', venue_name: '', visibility: 'public', season: new Date().getFullYear().toString(), banner_url: '',
  });

  const resetWizard = () => {
    setWizardStep(1);
    setNumTeams('4');
    setTeamForms([]);
    setSportFormats({});
    setFormData({ name: '', sport_codes: [], format: 'groups_plus_ko', start_date: '', end_date: '', venue_name: '', visibility: 'public', season: new Date().getFullYear().toString(), banner_url: '' });
  };

  // Helper: create sport formats for a tournament
  const saveSportFormats = async (slug, tournId, formats) => {
    const entries = Object.entries(formats);
    if (!entries.length) return;
    await Promise.all(entries.map(([code, fmt]) => {
      const sportObj = sports.find(s => s.code === code);
      return fetch(`/api/orgs/${slug}/tournaments/${tournId}/sport-formats`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('btccp_access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sport_code:        code,
          sport_name:        sportObj?.name || code.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()),
          singles_count:     fmt.singles_count  ?? 0,
          doubles_count:     fmt.doubles_count  ?? 0,
          platinum_count:    fmt.platinum_count ?? 0,
          win_pts:           fmt.win_pts        ?? 2,
          draw_pts:          fmt.draw_pts       ?? 1,
          loss_pts:          fmt.loss_pts       ?? 0,
        }),
      });
    }));
  };

  // Initialise sportFormats from selected sports + defaults
  const initSportFormats = (codes) => {
    const effectiveCodes = codes.length > 0 ? codes : sports.slice(0,1).map(s=>s.code);
    const formats = {};
    effectiveCodes.forEach(code => {
      formats[code] = { ...(SPORT_FORMAT_DEFAULTS[code] || { singles_count:3, doubles_count:2, platinum_count:2, win_pts:2, draw_pts:1, loss_pts:0 }) };
    });
    return formats;
  };

  // Create tournament immediately (without team setup)
  // Uses raw fetch() to bypass any axios interceptor issues
  const handleCreateNow = async () => {
    if (!formData.name.trim()) { toast.error('Please enter tournament name'); return; }
    const effectiveSports = formData.sport_codes.length > 0
      ? formData.sport_codes
      : sports.length > 0 ? [sports[0].code] : [];
    if (effectiveSports.length === 0) { toast.error('Please select at least one sport'); return; }

    setCreating(true);
    const payload = {
      name: formData.name,
      sport_code: effectiveSports[0],
      sport_codes: effectiveSports,
      format: formData.format || 'round_robin',
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      venue_name: formData.venue_name || null,
      visibility: formData.visibility || 'public',
    };

    // Get current token
    let token = localStorage.getItem('btccp_access_token');

    // Try to refresh token first to ensure it's fresh
    try {
      const refreshToken = localStorage.getItem('btccp_refresh_token');
      if (refreshToken) {
        const rRes = await fetch(`/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (rRes.ok) {
          const rData = await rRes.json();
          const newToken = rData?.tokens?.access_token || rData?.access_token;
          if (newToken) {
            token = newToken;
            localStorage.setItem('btccp_access_token', newToken);
            if (rData?.tokens?.refresh_token) localStorage.setItem('btccp_refresh_token', rData.tokens.refresh_token);
            console.log('[BTCCP] Token refreshed successfully');
          }
        }
      }
    } catch (refreshErr) {
      console.log('[BTCCP] Refresh failed (non-fatal):', refreshErr.message);
    }

    // Get org slug from /auth/me (raw fetch, not axios)
    let slugToUse = orgSlug || 'btcc';
    try {
      const meRes = await fetch(`/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        const freshSlug = me?.memberships?.[0]?.slug;
        if (freshSlug) {
          slugToUse = freshSlug;
          useAuthStore.getState().setActiveOrg(me?.default_org_id, freshSlug);
          console.log('[BTCCP] Fresh slug from /auth/me:', freshSlug, 'memberships:', me?.memberships?.length);
        } else {
          console.log('[BTCCP] /auth/me ok but no memberships. me:', JSON.stringify(me).slice(0,200));
        }
      } else {
        console.log('[BTCCP] /auth/me status:', meRes.status);
      }
    } catch (meErr) {
      console.log('[BTCCP] /auth/me fetch error:', meErr.message);
    }

    // Create tournament with raw fetch (bypasses axios interceptors entirely)
    try {
      console.log('[BTCCP] Creating tournament: slug=', slugToUse, 'token=', token?.slice(0,15));
      const res = await fetch(`/api/orgs/${slugToUse}/tournaments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        // Save sport formats (use currently configured or defaults)
        const fmts = Object.keys(sportFormats).length > 0 ? sportFormats : initSportFormats(effectiveSports);
        await saveSportFormats(slugToUse, data.id, fmts).catch(() => {});
        toast.success('Tournament created!');
        setCreateOpen(false);
        resetWizard();
        fetchTournaments();
      } else {
        console.error('[BTCCP] Create failed:', res.status, JSON.stringify(data));
        toast.error(`${data?.detail || 'Failed to create tournament'} (${res.status}, slug: ${slugToUse})`);
      }
    } catch (err) {
      console.error('[BTCCP] Fetch error:', err.message);
      toast.error(`Network error: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const goToStep2 = () => {
    if (!formData.name.trim()) { toast.error('Please enter tournament name'); return; }
    const effectiveCodes = formData.sport_codes.length > 0 ? formData.sport_codes : sports.length > 0 ? [sports[0].code] : [];
    if (effectiveCodes.length === 0) { toast.error('Please select at least one sport'); return; }
    setSportFormats(initSportFormats(effectiveCodes));
    setWizardStep(2);
  };

  const goToStep3 = () => {
    const n = Math.max(2, Math.min(64, parseInt(numTeams) || 4));
    const existing = teams.slice(0, n).map((t, i) => ({ ...emptyTeam(i), name: t.name, short_name: t.short_name || autoCode(t.name), logo_preview: t.logo_url || null }));
    const forms = Array.from({ length: n }, (_, i) => existing[i] || emptyTeam(i));
    setTeamForms(forms);
    setWizardStep(3);
  };

  useEffect(() => {
    fetchTournaments();
    fetchSports();
    fetchTeams();
  }, [orgSlug]);

  const fetchTeams = async () => {
    try {
      const res = await api.get(`/orgs/${orgSlug}/teams`);
      setTeams(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('Failed to load teams:', err?.message);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/tournaments`);
      setTournaments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    setSportsLoading(true);
    try {
      const response = await api.get(`/orgs/${orgSlug}/sports`);
      const orgSports = Array.isArray(response.data) ? response.data : [];
      if (orgSports.length > 0) {
        setSports(orgSports);
        setSportsLoading(false);
        return;
      }
    } catch (_) {
      // fall through to public sports
    }
    // Fallback: fetch global sports list
    try {
      const pubResponse = await api.get('/public/sports');
      setSports(Array.isArray(pubResponse.data) ? pubResponse.data : []);
    } catch (e) {
      console.error('Failed to fetch sports:', e);
    } finally {
      setSportsLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);

    // Build list of slugs to try from all available sources
    const slugsToTry = [];
    let liveSlug = orgSlug;
    try {
      const meRes = await api.get('/auth/me');
      const me = meRes.data;
      (me?.memberships || []).forEach(m => {
        if (m.slug) slugsToTry.push(m.slug);
        if (m.organization_id) slugsToTry.push(m.organization_id);
      });
      if (me?.default_org_id) slugsToTry.push(me.default_org_id);
      if (me?.memberships?.[0]?.slug) {
        liveSlug = me.memberships[0].slug;
        useAuthStore.getState().setActiveOrg(me?.default_org_id, liveSlug);
      }
      console.log('[BTCCP] handleCreate memberships:', JSON.stringify(me?.memberships?.map(m=>({slug:m.slug,id:m.organization_id}))));
    } catch (err) {
      console.warn('[BTCCP] Failed to refresh org slug before create:', err?.message);
    }
    [liveSlug, 'btcc', 'org_btcc', 'btccp', 'circles'].forEach(s => {
      if (s && !slugsToTry.includes(s)) slugsToTry.push(s);
    });

    const tournamentPayload = {
      name: formData.name,
      sport_code: formData.sport_codes[0] || undefined,
      sport_codes: formData.sport_codes.length > 0 ? formData.sport_codes : undefined,
      format: formData.format || 'round_robin',
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      venue_name: formData.venue_name || null,
      visibility: formData.visibility || 'public',
      season: formData.season || new Date().getFullYear().toString(),
    };

    const tryCreate = async (slug) => {
      // 1. Create tournament
      const tRes = await api.post(`/orgs/${slug}/tournaments`, tournamentPayload);
      const tournId = tRes.data.id;

      // 2. Save sport formats configured in Step 2
      await saveSportFormats(slug, tournId, sportFormats).catch(() => {});

      // 3. Create teams (if wizard step 3 was filled)
      const validTeams = teamForms.filter(tf => tf.name.trim());
      const createdTeamIds = [];

      for (const tf of validTeams) {
        try {
          const teamRes = await api.post(`/orgs/${slug}/teams`, {
            name: tf.name,
            short_name: tf.short_name || autoCode(tf.name),
            color: tf.color || null,
            tournament_id: tournId,
          });
          const teamId = teamRes.data.id;

          // Upload logo via PATCH if provided
          if (tf.logo_b64) {
            await api.patch(`/orgs/${slug}/teams/${teamId}`, { logo_b64: tf.logo_b64 }).catch(() => {});
          }

          createdTeamIds.push(teamId);
        } catch (_) { /* skip team on error */ }
      }

      // 3. Generate fixtures if we have ≥2 teams
      if (createdTeamIds.length >= 2) {
        const entries = createdTeamIds.map(id => ({ type: 'team', id }));
        await api.post(`/orgs/${slug}/tournaments/${tournId}/generate-fixtures`, {
          entries, seeding: 'linear',
        }).catch(() => {});
      }

      const msg = createdTeamIds.length > 0
        ? `Tournament created with ${createdTeamIds.length} teams!`
        : 'Tournament created!';
      toast.success(msg);
      setCreateOpen(false);
      resetWizard();
      fetchTournaments();
      fetchTeams();
    };

    try {
      // Try each slug until one works
      let created = false;
      let lastError = '';
      for (const slug of slugsToTry) {
        try {
          await tryCreate(slug);
          created = true;
          break;
        } catch (err) {
          lastError = err.response?.data?.detail || err.message || '';
          console.log('[BTCCP] Slug failed:', slug, lastError);
          if (err.response?.status !== 404) break;
        }
      }
      if (!created) {
        toast.error(`Failed to create tournament (tried: ${slugsToTry.join(', ')})`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tournamentId, name) => {
    if (!confirm(`Delete "${name}"?\n\nThis will permanently delete ALL data: encounters, groups, sport formats, sponsors, auction picks.\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/orgs/${orgSlug}/tournaments/${tournamentId}`);
      toast.success('Tournament and all related data deleted');
      fetchTournaments();
    } catch (err) {
      console.error('Failed to delete tournament:', err?.message);
      toast.error('Failed to delete tournament');
    }
  };

  const handleClone = (tournament) => {
    setCloneTarget(tournament);
    setCloneName(`TEST — ${tournament.name}`);
  };

  const confirmClone = async () => {
    if (!cloneTarget || !cloneName.trim()) return;
    setCloning(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${cloneTarget.id}/clone`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cloneName.trim(), copy_sponsors: true }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Cloned! ${data.sport_formats_copied} sport formats copied.`);
        toast.info('Go to the new tournament → Matches → Create Draw to start testing.');
        setCloneTarget(null);
        fetchTournaments();
      } else toast.error(data.detail || 'Clone failed');
    } catch (_) { toast.error('Clone failed'); }
    finally { setCloning(false); }
  };

  const openGenerateFixtures = (tournament) => {
    setGenerateTarget(tournament);
    setGenTeams([]);
    setGenSeeding('linear');
    setGenerateOpen(true);
  };

  const toggleGenTeam = (teamId) => {
    setGenTeams(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);
  };

  const handleGenerateFixtures = async () => {
    if (!generateTarget) return;
    if (genTeams.length < 2) { toast.error('Select at least 2 teams'); return; }
    setGenerating(true);
    try {
      const entries = genTeams.map(id => ({ type: 'team', id }));
      await api.post(`/orgs/${orgSlug}/tournaments/${generateTarget.id}/generate-fixtures`, {
        entries, seeding: genSeeding,
      });
      toast.success('Fixtures generated successfully!');
      setGenerateOpen(false);
      fetchTournaments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate fixtures');
    } finally {
      setGenerating(false);
    }
  };

  const filteredTournaments = tournaments.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-tournaments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Tournaments</h1>
          <p className="text-[#8a93a3]">Create and manage tournaments</p>
        </div>
        {admin && (
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] w-fit"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Tournament
        </Button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tournaments..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7]"
          />
        </div>
      </div>

      {/* Tournaments Grid */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-5">
                <Skeleton className="h-6 w-3/4 bg-[#1d2230] mb-3" />
                <Skeleton className="h-4 w-1/2 bg-[#1d2230] mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20 bg-[#1d2230]" />
                  <Skeleton className="h-6 w-24 bg-[#1d2230]" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTournaments.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTournaments.map(tournament => (
            <Card key={tournament.id} className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)] transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-[#f0f2f7] mb-1">{tournament.name}</h3>
                    {tournament.season && <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs mr-1">{tournament.season}</Badge>}
                    <p className="text-sm text-[#8a93a3]">
                      {tournament.sport_code?.replace('_', ' ')} • {tournament.format?.replace('_', ' ')}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8a93a3]">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#161a26] border-[#252a37]">
                      <DropdownMenuItem asChild className="text-[#f0f2f7] focus:bg-[#1d2230] cursor-pointer">
                        <Link to={`/admin/tournaments/${tournament.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Link>
                      </DropdownMenuItem>
                      {admin && (
                        <>
                          <DropdownMenuItem
                            onClick={() => openGenerateFixtures(tournament)}
                            className="text-[#f5c518] focus:bg-[#1d2230] cursor-pointer"
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            Generate Fixtures
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#252a37]" />
                          <DropdownMenuItem
                            onClick={() => handleClone(tournament)}
                            className="text-[#3b82f6] focus:bg-[#1d2230] cursor-pointer"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Clone for Testing
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#252a37]" />
                          <DropdownMenuItem
                            onClick={() => handleDelete(tournament.id, tournament.name)}
                            className="text-[#ef4444] focus:bg-[#1d2230] cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete (+ all data)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <StatusBadge status={tournament.status} />
                  {tournament.is_test && (
                    <Badge className="bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border border-[rgba(59,130,246,0.35)] text-xs font-bold">
                      🧪 TEST
                    </Badge>
                  )}
                  <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37]">
                    {tournament.fixtures_count || 0} matches
                  </Badge>
                  {tournament.entries_count > 0 && (
                    <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37]">
                      {tournament.entries_count} entries
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-[#8a93a3]">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(tournament.start_date)}</span>
                    {tournament.end_date && (
                      <span>- {formatDate(tournament.end_date)}</span>
                    )}
                  </div>
                </div>

                {tournament.venue_name && (
                  <p className="text-sm text-[#8a93a3] mt-2">📍 {tournament.venue_name}</p>
                )}

                <div className="mt-3 pt-3 border-t border-[#252a37] flex items-center justify-between flex-wrap gap-2">
                  <Link to={`/admin/tournaments/${tournament.id}`} className="text-xs text-[#f5c518] hover:text-[#f7d24a] flex items-center gap-1">
                    View Details →
                  </Link>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleClone(tournament)}
                      className="text-xs text-[#3b82f6] hover:text-[#60a5fa] flex items-center gap-1 bg-[rgba(59,130,246,0.08)] hover:bg-[rgba(59,130,246,0.15)] px-2.5 py-1 rounded-lg border border-[rgba(59,130,246,0.25)] transition-colors"
                      data-testid={`clone-tournament-${tournament.id}`}
                    >
                      <Copy className="h-3 w-3"/>Clone for Testing
                    </button>
                    <a
                      href={`/public/tournaments/${tournament.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#8a93a3] hover:text-[#f0f2f7] flex items-center gap-1"
                    >
                      Live Scoreboard ↗
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">No tournaments yet</h3>
            <p className="text-[#8a93a3] mb-4">Create your first tournament to get started</p>
            {admin && (
              <Button onClick={() => setCreateOpen(true)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                <Plus className="h-4 w-4 mr-2" />
                Create Tournament
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Tournament Wizard */}
      <Dialog open={createOpen} onOpenChange={(open) => {
        if (!open) resetWizard();
        setCreateOpen(open);
        if (open && sports.length === 0) fetchSports();
      }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[#f5c518]" />
              {wizardStep === 1 ? 'Create Tournament' : wizardStep === 2 ? 'Configure Sports' : 'Set Up Teams'}
            </DialogTitle>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-2">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= wizardStep ? 'bg-[#f5c518]' : 'bg-[#252a37]'}`} />
              ))}
              <span className="text-xs text-[#8a93a3] ml-1">Step {wizardStep}/3</span>
            </div>
          </DialogHeader>

          {wizardStep === 1 ? (
            /* ── STEP 1: Tournament Details ── */
            <div className="space-y-4">
              <div>
                <Label className="text-[#8a93a3]">Tournament Name *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Circles Sports League 2026"
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8a93a3]">Sports (optional — auto-selects first if skipped)</Label>
                  <div className="mt-2 space-y-2 p-3 bg-[#1d2230] rounded-lg border border-[#252a37] max-h-[160px] overflow-y-auto">
                    {sportsLoading ? (
                      <div className="flex items-center justify-center py-4 gap-2 text-[#8a93a3] text-sm">
                        <span className="animate-spin h-4 w-4 border-2 border-[#8a93a3] border-t-transparent rounded-full" />
                        Loading sports...
                      </div>
                    ) : sports.length === 0 ? (
                      <div className="py-4 text-center">
                        <p className="text-[#8a93a3] text-sm mb-2">No sports found</p>
                        <button type="button" onClick={fetchSports} className="text-xs text-[#f5c518] hover:text-[#f7d24a] underline">Retry</button>
                      </div>
                    ) : sports.map(sport => {
                      const isSelected = formData.sport_codes.includes(sport.code);
                      return (
                        <div key={sport.code} onClick={() => {
                          setFormData({ ...formData, sport_codes: isSelected
                            ? formData.sport_codes.filter(c => c !== sport.code)
                            : [...formData.sport_codes, sport.code] });
                        }} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-[rgba(245,197,24,0.15)]' : 'hover:bg-[#252a37]'}`}>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-[#f5c518] border-[#f5c518]' : 'border-[#8a93a3]'}`}>
                            {isSelected && <Check className="h-3 w-3 text-[#0b0f18]" />}
                          </div>
                          <span className={`text-sm ${isSelected ? 'text-[#f0f2f7] font-medium' : 'text-[#8a93a3]'}`}>{sport.name}</span>
                        </div>
                      );
                    })}
                  </div>
                  {formData.sport_codes.length > 0 && (
                    <p className="mt-1 text-xs text-[#f5c518]">{formData.sport_codes.length} sport{formData.sport_codes.length > 1 ? 's' : ''} selected</p>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[#8a93a3]">Format</Label>
                    <Select value={formData.format} onValueChange={v => setFormData({ ...formData, format: v })}>
                      <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#161a26] border-[#252a37]">
                        {FORMATS.map(f => <SelectItem key={f.value} value={f.value} className="text-[#f0f2f7] focus:bg-[#1d2230]">{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#8a93a3]">Number of Teams *</Label>
                    <Select value={numTeams} onValueChange={setNumTeams}>
                      <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
                        <Users className="h-4 w-4 mr-2 text-[#8a93a3]" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#161a26] border-[#252a37]">
                        {[2, 4, 6, 8, 10, 12, 16].map(n => (
                          <SelectItem key={n} value={String(n)} className="text-[#f0f2f7] focus:bg-[#1d2230]">{n} Teams</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8a93a3] mb-1 block">Start Date</Label>
                  <DatePickerField value={formData.start_date} onChange={d => setFormData({ ...formData, start_date: d })} placeholder="Start date" />
                </div>
                <div>
                  <Label className="text-[#8a93a3] mb-1 block">End Date</Label>
                  <DatePickerField value={formData.end_date} onChange={d => setFormData({ ...formData, end_date: d })} placeholder="End date" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8a93a3]">Venue</Label>
                  <Input value={formData.venue_name} onChange={e => setFormData({ ...formData, venue_name: e.target.value })}
                    placeholder="e.g., City Sports Complex" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]" />
                </div>
                <div>
                  <Label className="text-[#8a93a3]">Season / Year</Label>
                  <Input value={formData.season} onChange={e => setFormData({ ...formData, season: e.target.value })}
                    placeholder={new Date().getFullYear().toString()} className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]" />
                </div>
                <div>
                  <Label className="text-[#8a93a3]">Visibility</Label>
                  <Select value={formData.visibility} onValueChange={v => setFormData({ ...formData, visibility: v })}>
                    <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#161a26] border-[#252a37]">
                      <SelectItem value="public" className="text-[#f0f2f7] focus:bg-[#1d2230]">Public</SelectItem>
                      <SelectItem value="private" className="text-[#f0f2f7] focus:bg-[#1d2230]">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCreateNow}
                    disabled={creating}
                    className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]"
                    data-testid="create-tournament-only-button"
                  >
                    {creating ? 'Creating...' : 'Create Now'}
                  </Button>
                  <Button onClick={goToStep2} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                    data-testid="wizard-configure-sports-button">
                    Configure Sports <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </DialogFooter>
            </div>
          ) : wizardStep === 2 ? (
            /* ── STEP 2: Sport Formats — platinum + points ── */
            <div className="space-y-4">
              {/* Summary pill */}
              <div className="bg-[#1d2230] rounded-lg p-3 text-sm text-[#8a93a3]">
                <p className="text-[#f0f2f7] font-medium mb-0.5">{formData.name}</p>
                <p>{Object.keys(sportFormats).map(c => c.replace(/_/g,' ')).join(', ')} · {FORMATS.find(f => f.value === formData.format)?.label}</p>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[1fr_52px_52px_52px_40px_40px_40px] gap-2 px-1">
                <span className="text-[10px] text-[#8a93a3] uppercase tracking-wider font-bold">Sport</span>
                <span className="text-[10px] text-[#f5c518] uppercase tracking-wider font-bold text-center">Plat.</span>
                <span className="text-[10px] text-[#8a93a3] uppercase tracking-wider font-bold text-center">Sng</span>
                <span className="text-[10px] text-[#8a93a3] uppercase tracking-wider font-bold text-center">Dbl</span>
                <span className="text-[10px] text-[#22c55e] uppercase tracking-wider font-bold text-center">Win</span>
                <span className="text-[10px] text-[#8a93a3] uppercase tracking-wider font-bold text-center">Drw</span>
                <span className="text-[10px] text-[#ef4444] uppercase tracking-wider font-bold text-center">Lss</span>
              </div>

              {/* One row per sport */}
              <div className="space-y-2">
                {Object.entries(sportFormats).map(([code, fmt]) => {
                  const sportObj = sports.find(s => s.code === code);
                  const name = sportObj?.name || code.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
                  const upd = (field, val) => setSportFormats(prev => ({
                    ...prev, [code]: { ...prev[code], [field]: Math.max(0, parseInt(val) || 0) }
                  }));
                  return (
                    <div key={code} className="grid grid-cols-[1fr_52px_52px_52px_40px_40px_40px] gap-2 items-center px-3 py-2.5 bg-[#1d2230] rounded-xl border border-[#252a37]">
                      <span className="text-sm font-semibold text-[#f0f2f7] flex items-center gap-1.5">
                        <SportIcon sport={code} size={16}/>
                        <span className="truncate">{name}</span>
                      </span>
                      {/* Platinum count — highlighted in gold */}
                      <input type="number" min="0" max="10" value={fmt.platinum_count}
                        onChange={e => upd('platinum_count', e.target.value)}
                        title="Platinum players per team"
                        className="w-full h-9 bg-[#10131c] border border-[rgba(245,197,24,0.4)] rounded-lg text-center text-sm font-black text-[#f5c518] focus:outline-none focus:border-[#f5c518]" />
                      {/* Singles */}
                      <input type="number" min="0" max="20" value={fmt.singles_count}
                        onChange={e => upd('singles_count', e.target.value)}
                        title="Singles matches per tie"
                        className="w-full h-9 bg-[#10131c] border border-[#252a37] rounded-lg text-center text-sm text-[#f0f2f7] focus:outline-none focus:border-[#f5c518]" />
                      {/* Doubles */}
                      <input type="number" min="0" max="20" value={fmt.doubles_count}
                        onChange={e => upd('doubles_count', e.target.value)}
                        title="Doubles matches per tie"
                        className="w-full h-9 bg-[#10131c] border border-[#252a37] rounded-lg text-center text-sm text-[#f0f2f7] focus:outline-none focus:border-[#f5c518]" />
                      {/* Win pts */}
                      <input type="number" min="0" max="5" value={fmt.win_pts}
                        onChange={e => upd('win_pts', e.target.value)}
                        title="Points for a win"
                        className="w-full h-9 bg-[#10131c] border border-[rgba(34,197,94,0.3)] rounded-lg text-center text-sm font-bold text-[#22c55e] focus:outline-none focus:border-[#22c55e]" />
                      {/* Draw pts */}
                      <input type="number" min="0" max="5" value={fmt.draw_pts}
                        onChange={e => upd('draw_pts', e.target.value)}
                        title="Points for a draw"
                        className="w-full h-9 bg-[#10131c] border border-[#252a37] rounded-lg text-center text-sm text-[#8a93a3] focus:outline-none focus:border-[#f5c518]" />
                      {/* Loss pts */}
                      <input type="number" min="0" max="5" value={fmt.loss_pts}
                        onChange={e => upd('loss_pts', e.target.value)}
                        title="Points for a loss"
                        className="w-full h-9 bg-[#10131c] border border-[rgba(239,68,68,0.2)] rounded-lg text-center text-sm text-[#ef4444] focus:outline-none focus:border-[#ef4444]" />
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-start gap-2 px-3 py-2 bg-[rgba(245,197,24,0.05)] border border-[rgba(245,197,24,0.15)] rounded-xl">
                <Star className="h-3.5 w-3.5 text-[#f5c518] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#8a93a3] leading-relaxed">
                  <span className="text-[#f5c518] font-semibold">Plat.</span> = platinum players per team ·
                  <span className="text-[#8a93a3]"> Sng/Dbl = singles/doubles matches per tie ·</span>
                  <span className="text-[#22c55e] font-semibold"> Win</span>/<span className="text-[#8a93a3]">Drw</span>/<span className="text-[#ef4444] font-semibold">Lss</span> = points awarded
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setWizardStep(1)} className="border-[#252a37] text-[#f0f2f7]">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleCreateNow} disabled={creating}
                    className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]"
                    data-testid="create-tournament-only-button">
                    {creating ? 'Creating...' : 'Create Now'}
                  </Button>
                  <Button onClick={goToStep3} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                    Set Up Teams <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </DialogFooter>
            </div>
          ) : (
            /* ── STEP 3: Team Setup ── */
            <div className="space-y-4">
              <div className="bg-[#1d2230] rounded-lg p-3 text-sm text-[#8a93a3]">
                <p className="text-[#f0f2f7] font-medium mb-0.5">{formData.name}</p>
                <p>{formData.sport_codes.map(c => c.replace(/_/g,' ')).join(', ')} · {FORMATS.find(f => f.value === formData.format)?.label} · {numTeams} Teams</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#8a93a3]">Teams — Name, Code, Color, Logo, Sponsor</Label>
                  <span className="text-xs text-[#8a93a3]">{teamForms.filter(t => t.name).length}/{teamForms.length} named</span>
                </div>
                <ScrollArea className="max-h-[320px] pr-1">
                  <div className="space-y-2">
                    {teamForms.map((tf, i) => (
                      <TeamFormRow key={i} idx={i} data={tf}
                        onChange={(idx, updated) => setTeamForms(prev => prev.map((t, j) => j === idx ? updated : t))}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <p className="text-xs text-[#8a93a3]">
                Click the colored square to upload a logo. Teams with names will be created and fixtures generated automatically.
              </p>

              <DialogFooter>
                <Button variant="outline" onClick={() => setWizardStep(2)} className="border-[#252a37] text-[#f0f2f7]">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button onClick={handleCreate} disabled={creating || teamForms.filter(t => t.name.trim()).length < 2}
                  className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                  data-testid="wizard-create-tournament-button"
                >
                  {creating ? 'Creating...' : `Create Tournament & ${teamForms.filter(t => t.name.trim()).length} Teams`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Fixtures Dialog */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="generate-fixtures-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#f5c518]" />
              Generate Fixtures
              {generateTarget && (
                <span className="text-[#8a93a3] font-normal text-sm ml-1">— {generateTarget.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3] mb-2 block">
                Select Teams ({genTeams.length} selected)
              </Label>
              <ScrollArea className="h-[200px] bg-[#1d2230] rounded-lg border border-[#252a37]">
                {teams.length === 0 ? (
                  <div className="p-4 text-center text-[#8a93a3] text-sm">
                    No teams found. Add teams first.
                  </div>
                ) : (
                  teams.map((team) => {
                    const selected = genTeams.includes(team.id);
                    return (
                      <div
                        key={team.id}
                        onClick={() => toggleGenTeam(team.id)}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-[#252a37]/50 last:border-0 ${
                          selected ? 'bg-[rgba(245,197,24,0.08)]' : 'hover:bg-[#252a37]/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selected ? 'bg-[#f5c518] border-[#f5c518]' : 'border-[#8a93a3]'
                        }`}>
                          {selected && <Check className="h-3 w-3 text-[#0b0f18]" />}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${selected ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}>
                            {team.name}
                          </p>
                          {team.short_name && (
                            <p className="text-xs text-[#8a93a3]">{team.short_name}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </ScrollArea>
            </div>

            <div>
              <Label className="text-[#8a93a3] mb-1 block">Seeding</Label>
              <Select value={genSeeding} onValueChange={setGenSeeding}>
                <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#161a26] border-[#252a37]">
                  <SelectItem value="linear" className="text-[#f0f2f7] focus:bg-[#1d2230]">Linear (1 vs 2, 3 vs 4…)</SelectItem>
                  <SelectItem value="snake"  className="text-[#f0f2f7] focus:bg-[#1d2230]">Snake (1 vs last, 2 vs 2nd-last…)</SelectItem>
                  <SelectItem value="random" className="text-[#f0f2f7] focus:bg-[#1d2230]">Random Draw</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button
              onClick={handleGenerateFixtures}
              disabled={generating || genTeams.length < 2}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="generate-fixtures-submit-button"
            >
              {generating ? 'Generating...' : `Generate (${genTeams.length} team${genTeams.length !== 1 ? 's' : ''})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Clone Tournament Modal ─────────────────────────── */}
      <Dialog open={!!cloneTarget} onOpenChange={(o) => { if (!o) setCloneTarget(null); }}>
        <DialogContent className="bg-[#10131c] border-[#252a37] text-[#f0f2f7]">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Clone Tournament</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#8a93a3]">
              Creates a copy of <span className="text-[#f5c518] font-semibold">{cloneTarget?.name}</span> with all sport formats.
              Teams and players carry over automatically.
            </p>
            <div>
              <Label className="text-[#8a93a3] mb-1.5 block">Name for the cloned tournament</Label>
              <Input
                value={cloneName}
                onChange={e => setCloneName(e.target.value)}
                placeholder="e.g. TEST — Circles Sports League 2025"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] focus:border-[#f5c518]"
                onKeyDown={e => e.key === 'Enter' && confirmClone()}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneTarget(null)}
              className="border-[#252a37] text-[#8a93a3]">Cancel</Button>
            <Button onClick={confirmClone} disabled={cloning || !cloneName.trim()}
              className="bg-[#f5c518] text-[#0b0f18] font-bold hover:bg-[#f7d24a]">
              {cloning ? 'Cloning…' : 'Clone Tournament'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
